import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { createOrder, errMsg, listProducts } from "@/lib/api";
import { money } from "@/lib/format";

export default function NewOrderDialog({ open, onOpenChange, conversation, onCreated }) {
  const [products, setProducts] = useState([]);
  const [qty, setQty] = useState({});
  const [type, setType] = useState("pedido");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQty({}); setNotes(""); setType("pedido");
    listProducts().then((p) => setProducts(p.filter((x) => x.available))).catch(() => {});
  }, [open]);

  const bump = (id, d) => setQty((q) => ({ ...q, [id]: Math.max(0, (q[id] || 0) + d) }));
  const items = products.filter((p) => qty[p.id]).map((p) => ({ name: p.name, qty: qty[p.id], price: p.price }));
  const total = items.reduce((s, i) => s + i.qty * i.price, 0);

  const save = async () => {
    setSaving(true);
    try {
      await createOrder({ conversation_id: conversation.id, contact_name: conversation.contact_name || "", type, items, notes });
      toast.success(type === "reserva" ? "Reserva creada" : "Pedido creado");
      onCreated(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-order-dialog">
        <DialogHeader><DialogTitle className="font-display">Nuevo registro para {conversation.contact_name || "el cliente"}</DialogTitle></DialogHeader>
        <div className="flex gap-2">
          {["pedido", "reserva"].map((t) => (
            <button key={t} data-testid={`order-type-${t}`} onClick={() => setType(t)}
              className={`flex-1 h-9 rounded-full text-sm font-semibold border transition-colors ${type === t ? "bg-[#111B21] text-white border-[#111B21]" : "border-slate-200"}`}>
              {t === "pedido" ? "Pedido" : "Reserva"}
            </button>
          ))}
        </div>
        <div className="max-h-64 overflow-y-auto scroll-thin divide-y divide-slate-100">
          {products.length === 0 && <p className="text-sm text-slate-400 py-4">No hay productos disponibles en el catálogo.</p>}
          {products.map((p) => (
            <div key={p.id} className="flex items-center gap-3 py-2">
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{p.name}</p><p className="text-xs text-slate-500">{money(p.price)}</p></div>
              <Button size="icon" variant="outline" className="h-7 w-7 rounded-full" data-testid={`order-dec-${p.id}`} onClick={() => bump(p.id, -1)}><Minus className="h-3 w-3" /></Button>
              <span className="w-5 text-center text-sm font-bold" data-testid={`order-qty-${p.id}`}>{qty[p.id] || 0}</span>
              <Button size="icon" variant="outline" className="h-7 w-7 rounded-full" data-testid={`order-inc-${p.id}`} onClick={() => bump(p.id, 1)}><Plus className="h-3 w-3" /></Button>
            </div>
          ))}
        </div>
        <div><Label>Notas (dirección, fecha, hora…)</Label><Textarea data-testid="order-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1.5" /></div>
        <DialogFooter className="items-center sm:justify-between">
          <span className="font-display text-lg font-extrabold" data-testid="new-order-total">{money(total)}</span>
          <Button data-testid="create-order-button" onClick={save} disabled={saving || (type === "pedido" && items.length === 0)} className="rounded-full bg-emerald-500 hover:bg-emerald-600">
            {saving ? "Guardando…" : "Crear"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
